"use client";

import Link from "next/link";
import SectionHeader from "@/components/ui/section-header";
import UseCasesGrid, { type UseCaseGridItem } from "@/components/sections/shared/use-cases-grid";
import {
  AdVisual,
  AgentsVisual,
  AiVisual,
  BrandVisual,
  GeoVisual,
  MarketVisual,
  PriceVisual,
  ScrapingVisual,
  SeoVisual,
} from "@/components/sections/shared/use-cases-grid/card-visuals";
import { ROUTES } from "@/lib/constants";

// German clone of residential-proxies/use-cases — same grid/visuals,
// translated copy.

const RESIDENTIAL_USE_CASES_DE: UseCaseGridItem[] = [
  {
    id: "web-scraping",
    label: "Web Scraping",
    headline: "Öffentliche Websites zuverlässig und im großen Stil scrapen.",
    description:
      "Strukturierte Daten in großem Umfang von öffentlichen Websites extrahieren. Unser Netzwerk rotierender Residential-IPs fügt sich in den alltäglichen Datenverkehr ein und verbessert die Zugriffszuverlässigkeit für geeignete Workloads.",
    href: "/use-cases/web-scraping",
    Visual: ScrapingVisual,
    span: "anchor",
  },
  {
    id: "price-monitoring",
    label: "Preisüberwachung",
    headline: "Wettbewerberpreise in Echtzeit verfolgen.",
    description:
      "Wettbewerberpreise über Tausende Angebote hinweg verfolgen, mit präzisen, standortgenauen Ergebnissen — frische, zuverlässige Daten im großen Stil erfasst.",
    href: "/use-cases/price-monitoring",
    Visual: PriceVisual,
  },
  {
    id: "seo-monitoring",
    label: "SEO-Monitoring",
    headline: "SERP-Rankings von jedem Standort aus verfolgen.",
    description:
      "Echte Keyword-Rankings aus jedem Land, jeder Stadt oder Suchmaschine prüfen. Sehen, was die Zielgruppe sieht.",
    href: "/use-cases/seo-monitoring",
    Visual: SeoVisual,
  },
  {
    id: "ai-llm-training",
    label: "KI- und LLM-Training",
    headline: "KI-Modelle mit frischen Webdaten versorgen.",
    description:
      "Vielfältige, geo-gezielte Webdaten für Training und Fine-Tuning sammeln — zuverlässiger Zugriff im großen Stil, ohne Verzerrung durch begrenzte Geografie.",
    href: "/use-cases/ai-llm-training/",
    Visual: AiVisual,
  },
  {
    id: "ai-agents",
    label: "KI-Agenten",
    headline: "Agenten einen Workflow geben, nicht nur eine IP.",
    description:
      "Ein Agent fordert eine Residential-IP im Zielland an, ruft eine lokalisierte Produktseite ab, hält für die zusammengehörigen Anfragen eine sticky Session und übergibt die gesammelten Daten an die Anwendung. Land, Stadt und Session-Modus werden über die REST-API oder den MCP-Server konfiguriert.",
    href: "/proxy-server/residential-proxies#residential-for-developers-heading",
    Visual: AgentsVisual,
  },
  {
    id: "market-research",
    label: "Marktforschung",
    headline: "Märkte aus jeder Geografie heraus verstehen.",
    description:
      "Regionsspezifische Inhalte, lokale Anzeigen und lokalisierte Preise so erleben, wie Nutzer in diesem Markt es tun.",
    href: "/use-cases/market-research/",
    Visual: MarketVisual,
  },
  {
    id: "brand-protection",
    label: "Markenschutz",
    headline: "Die eigene Marke im gesamten Web überwachen.",
    description:
      "Fälschungen, nicht autorisierte Verkäufer und Markenrechtsverletzungen über globale Marktplätze hinweg gleichzeitig aus mehreren Standorten erkennen.",
    href: "/use-cases/brand-protection-monitoring/",
    Visual: BrandVisual,
  },
  {
    id: "ad-verification",
    label: "Ad Verification",
    headline: "Die in jedem Markt ausgespielten Anzeigen sehen.",
    description:
      "Anzeigen, Landingpages und Kampagnen-Compliance aus jeder Region überwachen — sehen, was echten Nutzern in jedem Markt gezeigt wird.",
    href: "/use-cases/ad-verification/",
    Visual: AdVisual,
  },
  {
    id: "geolocation-testing",
    label: "Geolocation Testing",
    headline: "Die eigene Website bis auf Stadt- und ISP-Ebene testen.",
    description:
      "Websites, Apps, Preise und Inhalte so testen, wie Nutzer sie in jedem Markt sehen, bis auf Stadt- und ISP-Ebene.",
    href: "/use-cases/localized-testing/",
    Visual: GeoVisual,
  },
];

export default function ResidentialUseCasesDe() {
  return (
    <section
      id="residential-use-cases"
      className="py-section border-border border-b"
      aria-labelledby="residential-use-cases-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <SectionHeader
            eyebrow="Anwendungsfälle"
            id="residential-use-cases-heading"
            heading="Residential Proxies für Web Scraping und Datenerfassung"
            body="Die richtige IP, vom richtigen Standort, für jeden Workflow, der auf zuverlässigen Zugriff angewiesen ist."
          />
        </div>

        <UseCasesGrid items={RESIDENTIAL_USE_CASES_DE} />

        <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
          Nicht sicher, ob das passt?{" "}
          <Link
            href={ROUTES.useCases}
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            Proxy-Workflows für jeden Anwendungsfall vergleichen
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
